import { Logger } from "./logger";
import {
  URL_PARAM_REF,
  URL_PARAM_CT,
  URL_PARAM_DISCOUNT,
  URL_MAX_PARAM_LENGTH,
  URL_SAFE_CODE_PATTERN,
} from "./config";
import type { TrackingParams } from "../types";

export function parseTrackingParams(): TrackingParams {
  const empty: TrackingParams = {
    ref: null,
    ct: null,
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
    gclid: null,
    fbclid: null,
    msclkid: null,
    discount: null,
  };

  if (typeof window === "undefined" || !window.location) {
    Logger.warn("URL parsing skipped — window.location not available");
    return empty;
  }

  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    Logger.error("Failed to parse URL search params");
    return empty;
  }

  const result: TrackingParams = {
    ref: sanitizeCode(params.get(URL_PARAM_REF)),
    ct: sanitizeCode(params.get(URL_PARAM_CT)),
    utm_source: sanitizeParam(params.get("utm_source")),
    utm_medium: sanitizeParam(params.get("utm_medium")),
    utm_campaign: sanitizeParam(params.get("utm_campaign")),
    utm_content: sanitizeParam(params.get("utm_content")),
    utm_term: sanitizeParam(params.get("utm_term")),
    gclid: sanitizeParam(params.get("gclid")),
    fbclid: sanitizeParam(params.get("fbclid")),
    msclkid: sanitizeParam(params.get("msclkid")),
    discount: sanitizeCode(params.get(URL_PARAM_DISCOUNT)),
  };

  if (result.ref) {
    Logger.log(`Detected affiliate ref: ${result.ref}`);
  }
  if (result.discount) {
    Logger.log(`Detected discount code: ${result.discount}`);
  }

  return result;
}

export function stripTrackingParams(): void {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  try {
    const url = new URL(window.location.href);
    let modified = false;
    for (const p of ["ref", "ct", "hs_ct", "hs_click"]) {
      if (url.searchParams.has(p)) {
        url.searchParams.delete(p);
        modified = true;
      }
    }
    if (modified) {
      window.history.replaceState(window.history.state, "", url.toString());
    }
  } catch {
    Logger.warn("Failed to strip tracking params from URL");
  }
}

function sanitizeCode(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > URL_MAX_PARAM_LENGTH) return null;
  if (!URL_SAFE_CODE_PATTERN.test(trimmed)) {
    Logger.warn(`Invalid tracking code rejected: ${trimmed.substring(0, 20)}...`);
    return null;
  }
  return trimmed;
}

function sanitizeParam(value: string | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed.length === 0 || trimmed.length > URL_MAX_PARAM_LENGTH) return null;
  const clean = trimmed.replace(/<[^>]*>/g, "");
  if (clean.length === 0) return null;
  return clean;
}
